import { isEmptyString } from '@/utils/string';
import type { LoginStep } from '../login-flow';

export type StepPrerequisites = {
  email: string;
  ssoConfiguration: unknown;
  chooseUserEntries: readonly unknown[];
  /** True once the email step has actually been submitted this session (SSO discovery ran). */
  hasSubmittedEmail: boolean;
};

/**
 * Password / SSO / chooser depend on state established by the email step, which
 * only lives in memory. On a refresh or direct visit to e.g. `/login/sso` that
 * state is gone, so fall back to the email step instead of rendering a blank or
 * unusable panel. The 2FA email-link step is exempt — it's reachable directly
 * via the magic link's `?token=`.
 *
 * SSO accounts never see the password form: when an SSO configuration is still
 * in memory (e.g. the user navigated back from the SSO step to `/login/password`)
 * the password/2FA steps redirect to `sso`, matching Ember where `submitEmail`
 * routed SSO emails straight to SSO and never to a password prompt.
 *
 * The password/2FA steps additionally require the email step to have actually
 * been submitted this session (`hasSubmittedEmail`). A prefilled `?email=` seeds
 * the email in memory but is *not* a submission — so a direct visit to
 * `/login/password?email=…` falls back to the email step (which auto-submits and
 * runs SSO discovery) rather than stranding an SSO-only account on the password
 * form. This mirrors Ember, where those routes redirected to `login.index` and
 * the sticky `?email` auto-submitted there.
 */
export function resolveEffectiveStep(
  step: LoginStep,
  { email, ssoConfiguration, chooseUserEntries, hasSubmittedEmail }: StepPrerequisites,
): LoginStep {
  switch (step) {
    case 'password':
    case 'two_factor_authentication':
      if (!hasSubmittedEmail || isEmptyString(email)) return 'email';
      return ssoConfiguration == null ? step : 'sso';
    case 'sso':
      return ssoConfiguration == null ? 'email' : step;
    case 'choose_user':
      return chooseUserEntries.length === 0 ? 'email' : step;
    default:
      return step;
  }
}
